<template>
  <div>
    <b-row class="mb-3">
      <b-col class="d-flex justify-content-end">
        <b-button
          v-if="!isSupplier"
          variant="gradient-primary"
          :to="{ name: 'exchange-manifest-create' }"
          class="ml-1"
        >
          <feather-icon icon="PlusIcon" />
          <span class="ml-1">Ajouter manifeste d'échange</span>
        </b-button>
      </b-col>
    </b-row>

    <div
      v-if="isLoading"
      class="text-center mb-2"
    >
      <b-spinner
        variant="primary"
        label="Chargement..."
      />
      <p class="mt-2">
        Chargement des manifestes d'échange...
      </p>
    </div>

    <b-card
      v-else-if="!isLoading && manifests.length === 0"
      class="text-center"
    >
      <b-card-body class="py-5">
        <feather-icon
          icon="PackageIcon"
          size="48"
          class="text-muted mb-3"
        />
        <h4 class="text-muted">
          Aucun manifeste d'échange
        </h4>
        <p class="text-muted mb-3">
          Il n'y a pas encore de manifestes d'échange dans le système.
        </p>
        <b-button
          v-if="!isSupplier"
          :to="{ name: 'exchange-manifest-create' }"
          variant="primary"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-1"
          />
          Créer le premier manifeste d'échange
        </b-button>
      </b-card-body>
    </b-card>

    <vue-good-table
      v-else
      :columns="columns"
      :rows="manifests"
      :search-options="{ enabled: true, externalQuery: searchTerm }"
      :pagination-options="{ enabled: true, perPage: pageLength }"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <div v-if="props.column.field === 'id'">
          <qrcode-vue
            :value="props.row.id.toString()"
            :size="size"
            level="H"
          />
          <p>{{ props.row.id }}</p>
        </div>

        <span v-else-if="props.column.field === 'orders_count'">
          <b-badge
            variant="warning"
            pill
          >
            {{ props.row.orders_count }}
          </b-badge>
        </span>

        <span v-else-if="props.column.field === 'action'">
          <b-button
            size="sm"
            variant="outline-info"
            :to="{ name: 'exchange-manifest-detail', params: { id: props.row.id } }"
            class="mr-1"
          >
            <feather-icon
              icon="EyeIcon"
              class="mr-1"
            />
            Voir
          </b-button>
          <b-button
            v-if="isSupplier && props.row.receivable_orders_count > 0"
            size="sm"
            variant="success"
            :disabled="isReceivingManifest(props.row.id)"
            @click="markManifestAsReceived(props.row)"
          >
            <b-spinner
              v-if="isReceivingManifest(props.row.id)"
              small
              class="mr-1"
            />
            <span>Reçu</span>
          </b-button>
          <b-badge
            v-else-if="isSupplier && props.row.all_orders_received"
            variant="light-success"
            class="ml-1"
          >
            Reçu ✓
          </b-badge>
        </span>

        <span v-else>
          {{ props.formattedRow[props.column.field] || '-' }}
        </span>
      </template>

      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap">Affichage 1 à</span>
            <b-form-select
              v-model="pageLength"
              :options="['50','100','500']"
              class="mx-1"
              @input="(value) => props.perPageChanged({ currentPerPage: value })"
            />
            <span class="text-nowrap">de {{ count }} entrées</span>
          </div>

          <div>
            <b-pagination
              v-model="currentPage"
              :total-rows="count"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @input="pageNext"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>
  </div>
</template>

<script>
import {
  BPagination,
  BFormSelect,
  BButton,
  BSpinner,
  BCol,
  BRow,
  BCard,
  BCardBody,
  BBadge,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import axios from 'axios'
import QrcodeVue from 'qrcode.vue'
import storeAuth from '@/store/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'ExchangeManifestIndex',
  components: {
    VueGoodTable,
    BPagination,
    BFormSelect,
    BButton,
    BSpinner,
    QrcodeVue,
    BCol,
    BRow,
    BCard,
    BCardBody,
    BBadge,
  },
  data() {
    return {
      size: 40,
      manifests: [],
      isLoading: false,
      pageLength: 100,
      searchTerm: '',
      currentPage: 1,
      count: 0,
      user: {},
      receivingManifestIds: [],
      columns: [
        {
          label: 'Reference',
          field: 'id',
        },
        {
          label: 'Date',
          field: 'created_at',
          formatFn: this.formatFn,
        },
        {
          label: 'Créé par',
          field: 'created_by_username',
        },
        {
          label: 'Nombre de colis',
          field: 'orders_count',
        },
        {
          label: 'Note',
          field: 'note',
        },
        {
          label: 'Action',
          field: 'action',
        },
      ],
    }
  },
  computed: {
    userRole() {
      return storeAuth.getters.userRole || this.user.role
    },
    isSupplier() {
      return this.userRole === 'supplier'
    },
  },
  created() {
    this.user = storeAuth.state.user
    this.getManifests(1)
  },
  methods: {
    pageNext(currentPage) {
      this.getManifests(currentPage)
    },
    formatFn(date) {
      if (!date) return null
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(2, '0')} | ${h}`
    },
    async getManifests(currentPage) {
      this.isLoading = true
      try {
        const { data } = await axios.get('/api/exchange-manifests/', {
          params: {
            page: currentPage,
            page_size: this.pageLength,
          },
        })
        this.manifests = data.results || data
        this.count = data.count || this.manifests.length
      } catch (error) {
        this.manifests = []
        this.count = 0
      } finally {
        this.isLoading = false
      }
    },
    isReceivingManifest(manifestId) {
      return this.receivingManifestIds.includes(manifestId)
    },
    async markManifestAsReceived(manifest) {
      if (!this.isSupplier || this.isReceivingManifest(manifest.id)) {
        return
      }

      this.receivingManifestIds.push(manifest.id)
      try {
        await axios.post(`/api/exchange-manifests/${manifest.id}/mark-received/`)
        this.showToast('success', 'top-center', `Manifeste d'échange #${manifest.id} marqué comme reçu`)
        await this.getManifests(this.currentPage)
      } catch (error) {
        const message = error.response?.data?.error || error.response?.data?.detail || 'Erreur lors de la mise à jour du manifeste'
        this.showToast('danger', 'top-center', message)
      } finally {
        this.receivingManifestIds = this.receivingManifestIds.filter(id => id !== manifest.id)
      }
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: variant === 'success' ? 'Succès' : 'Erreur',
            icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertCircleIcon',
            text,
            variant,
          },
        },
        { position },
      )
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
